import * as React from "react";
import {
    MessageBoxOptionsEnum,
    MessageSearchCriteria,
    MessageSearchResults,
} from "../..";
import { createError, RelodeApiError } from "../../../../";
import { useMessageCheckerContext } from "../../messageChecker/messageCheckerProvider";
import {
    getAllMessages,
    getReceivedMessages,
    getSentMessages,
    setMessagesRead,
    setMessagesUnread,
} from "../../messaging.service";
import {
    creators,
    MessageFacet,
    MessageSearchActionTypes,
} from "./messageListActionTypes";
import { MessageListingState } from "./messageListingState";

export const useMessageSearchActions = (
    state: MessageListingState,
    dispatch: React.Dispatch<MessageSearchActionTypes>
) => {
    const {
        state: { unreadCount },
        actions: { updateUnreadCount },
    } = useMessageCheckerContext();

    const search = async (criteria: MessageSearchCriteria) => {
        dispatch(creators.search(criteria));
        try {
            let results: MessageSearchResults;

            switch (criteria.box) {
                case MessageBoxOptionsEnum.Inbox.name: {
                    results = await getReceivedMessages(criteria);
                    break;
                }
                case MessageBoxOptionsEnum.Sent.name: {
                    results = await getSentMessages(criteria);
                    break;
                }
                case MessageBoxOptionsEnum.AllMessages.name: {
                    results = await getAllMessages(criteria);
                    break;
                }
            }
            dispatch(creators.searchSuccess(results));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.searchFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "core.messageListingHandlers.useAsyncEffect:: get sent or received messages"
            );
        }
    };

    const markSelectedUnread = async () => {
        if (state.selectedMessages.length < 1) return;
        try {
            await setMessagesUnread(state.selectedMessages);
            await search(state.searchCriteria);
            updateUnreadCount(unreadCount + state.selectedMessages.length);
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) return;
            }
            throw createError(
                err,
                "core.messages.message-listing.messageListingActions.markSelectedUnread"
            );
        }
    };

    const markSelectedRead = async () => {
        if (state.selectedMessages.length < 1) return;
        try {
            await setMessagesRead(state.selectedMessages);
            await search(state.searchCriteria);
            updateUnreadCount(unreadCount - state.selectedMessages.length);
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) return;
            }
            throw createError(
                err,
                "core.messages.message-listing.messageListingActions.markSelectedRead"
            );
        }
    };

    const filterMessages = (facet: MessageFacet) =>
        dispatch(creators.filter(facet));
    const sort = (field: string) => {
        dispatch(creators.sort(field));
    };

    const setSearchCriteria = (criteria: MessageSearchCriteria) =>
        dispatch(creators.setSearchCriteria(criteria));

    const navigate = (direction: "next" | "prev") =>
        dispatch(creators.navigate(direction));

    const selectMessage = (id: number | string) =>
        dispatch(creators.selectMessage(+id));
    const unselectMessage = (id: number | string) =>
        dispatch(creators.unselectMessage(+id));
    const selectAllMessages = () => dispatch(creators.selectAllMessages());
    const deselectAllMessages = () => dispatch(creators.deselectAllMessages());
    return {
        navigate,
        search,
        setSearchCriteria,
        sort,
        selectMessage,
        unselectMessage,
        selectAllMessages,
        deselectAllMessages,
        markSelectedUnread,
        markSelectedRead,
        filterMessages,
    };
};

export type MessageSearchActions = ReturnType<typeof useMessageSearchActions>;
